// @workflow assistant.ui-tools
/**
 * The task board UI tool: `ui.tasks.open_on_board { taskId, facilityId? }` opens a task on its task
 * board with its Edit task open, through the app's one action for it (actions/openTaskOnBoard.js, the
 * timeline popover's "Open in task board"). Never a share-intent URL (AVA-711, AVA-753).
 *
 * - The board is the task's own: read from the task (`api/Tasks/{id}` → `typeId`), not from the model.
 * - The facility is `facilityId`, else the one in view. Another facility than the one in view is
 *   opened first in this tab (viewerPrepare.js PREPARE_NEED.FACILITY, as the tag tools do), so the
 *   board opens here and can be read back; the shared action would otherwise use a new tab.
 * - "Opened" is said only after reading the screen back: the task's board is the page, and Edit task
 *   shows this task (actions/openTask.js). Otherwise not_available in plain words.
 *
 * Success: `{ opened: true }`. `ui.tasks.show_on_board { taskId, facilityId? }` (Khalid, 2026-10-21) shows the task
 * instead: the board with its card highlighted and scrolled into view, Edit task closed (actions/openTaskOnBoard.js
 * showTaskOnBoard, an in-app request); success `{ shown: true }` once the board registered the card
 * (actions/shownBoardTask.js).
 * Host contract (`host.taskBoard`, services/taskBoardHost.js):
 *   facilityInView() → string|null             the facility whose pages are open
 *   boardOf(taskId) → Promise<string|null>      the task's board id, null when the app has no such task
 *   open({ taskId, taskTypeId, facilityId }) → Promise<{ status }>   the shared action (TASK_BOARD_OPEN)
 *   show({ taskId, taskTypeId, facilityId }) → Promise<{ status }>   the show action (TASK_BOARD_OPEN)
 *   readBoard() → { taskTypeId, shownTaskId, openTaskId }   the board on the page, its highlighted card, the task in Edit task
 * and `host.viewer` for the facility hop (viewerPrepare.js).
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { UI_TOOL, ok, notAvailable, failed } from './uiToolContract.js';
import { VIEWER_DETAIL, VIEWER_WAIT, sameId } from './viewerContract.js';
import { cleanFacilityId, cleanIdList } from './viewerArgs.js';
import { PREPARE_NEED, prepareViewer, deadlineOf, SYSTEM_CLOCK } from './viewerPrepare.js';
import { TASK_LIMITS } from './taskTools.js';
import { TASK_BOARD_OPEN } from '../../../actions/openTaskOnBoard.js';
import { CAPABILITY } from '../../../actions/permissions.js';

/** How long the board may take to show the task (the request's expiry can end it sooner). */
export const TASK_BOARD_WAIT = Object.freeze({ TIMEOUT_MS: 30 * 1000 });

/** Plain words for the model. */
export const TASK_BOARD_DETAIL = Object.freeze({
  NO_BOARD: "The task board can't be opened from here.",
  NO_FACILITY: "AVA doesn't know which facility this task is in. Open the facility, then try again.",
  NOT_FOUND: "AVA couldn't find this task.",
  BOARD_NOT_OPENED: "The task board didn't open. Try again in a moment.",
  TASK_NOT_OPENED: "The task board opened, but the task didn't open on it. Find it on the board.",
  TASK_NOT_SHOWN: "The task board opened, but the task isn't shown on it. Find it on the board.",
});

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const boardSide = (host) => (host && host.taskBoard) || null;

/** `{ taskId, facilityId? }` → `{ taskId, facilityId }` or null. */
export function checkOpenOnBoardArgs(args) {
  if (!isPlainObject(args)) return null;
  const ids = cleanIdList([args.taskId], { maxCount: 1, maxLength: TASK_LIMITS.MAX_TASK_ID_LENGTH });
  const facilityId = cleanFacilityId(args.facilityId);
  if (!ids || facilityId === undefined) return null;
  return { taskId: ids[0], facilityId };
}

/** The task's facility: the one named, else the one in view. */
const facilityOf = (host, input) => input.facilityId || boardSide(host).facilityInView();

/** Another facility than the one in view: open it first, in this tab. None at all: say so. */
function prepareBoard(host, input) {
  const board = boardSide(host);
  if (!board) return notAvailable(TASK_BOARD_DETAIL.NO_BOARD);
  const target = facilityOf(host, input);
  if (!target) return notAvailable(TASK_BOARD_DETAIL.NO_FACILITY);
  const inView = board.facilityInView();
  return inView && !sameId(inView, target) ? prepareViewer(host, target, PREPARE_NEED.FACILITY) : null;
}

/** Show reads back the highlighted card; open reads back Edit task. */
const SHOWN_BY = Object.freeze({
  open: { field: 'openTaskId', missing: TASK_BOARD_DETAIL.TASK_NOT_OPENED, result: { opened: true } },
  show: { field: 'shownTaskId', missing: TASK_BOARD_DETAIL.TASK_NOT_SHOWN, result: { shown: true } },
});

/** Wait, bounded and stoppable, until the task's board shows it (in Edit task for open, highlighted for show). */
async function readBack(host, { taskId, taskTypeId }, by = SHOWN_BY.open) {
  const clock = host.clock || SYSTEM_CLOCK;
  const signal = host.signal || null;
  const deadline = deadlineOf(clock.now(), host.expiresAt, TASK_BOARD_WAIT.TIMEOUT_MS);
  for (;;) {
    if (signal && signal.aborted) return failed(VIEWER_DETAIL.STOPPED);
    const screen = boardSide(host).readBoard() || {};
    const onBoard = sameId(screen.taskTypeId, taskTypeId);
    if (onBoard && sameId(screen[by.field], taskId)) return ok(by.result);
    if (clock.now() >= deadline) return notAvailable(onBoard ? by.missing : TASK_BOARD_DETAIL.BOARD_NOT_OPENED);
    await clock.sleep(VIEWER_WAIT.POLL_MS, signal);
  }
}

async function onBoard(input, host, mode) {
  const board = boardSide(host);
  const taskTypeId = await board.boardOf(input.taskId);
  // Stopped while the task was read: open nothing, the page now belongs to whatever came next.
  if (host.signal && host.signal.aborted) return failed(VIEWER_DETAIL.STOPPED);
  if (!taskTypeId) return notAvailable(TASK_BOARD_DETAIL.NOT_FOUND);
  const task = { taskId: input.taskId, taskTypeId, facilityId: facilityOf(host, input) };
  const opened = await (mode === SHOWN_BY.show ? board.show(task) : board.open(task));
  if (!opened || opened.status !== TASK_BOARD_OPEN.OPENING) return notAvailable(TASK_BOARD_DETAIL.BOARD_NOT_OPENED);
  return readBack(host, { taskId: input.taskId, taskTypeId }, mode);
}

/** `ui.tasks.open_on_board`: the task in Edit task on its board ("open the task"). */
const openOnBoard = (input, host) => onBoard(input, host, SHOWN_BY.open);
/** `ui.tasks.show_on_board`: the task's card highlighted on its board, Edit task closed ("show it on the taskboard"). */
const showOnBoard = (input, host) => onBoard(input, host, SHOWN_BY.show);

/** Registry entries. `capability`: the runner checks `host.can` after `check`, before `ready`. */
export const TASK_BOARD_ACTIONS = Object.freeze({
  [UI_TOOL.TASKS_OPEN_ON_BOARD]: {
    capability: CAPABILITY.TASKS_OPEN_BOARD,
    check: checkOpenOnBoardArgs,
    ready: prepareBoard,
    run: openOnBoard,
  },
  [UI_TOOL.TASKS_SHOW_ON_BOARD]: {
    capability: CAPABILITY.TASKS_OPEN_BOARD,
    check: checkOpenOnBoardArgs,
    ready: prepareBoard,
    run: showOnBoard,
  },
});
